import React from "react";
import CrudManager from "../../../../components/common/CrudManager";
import countiesService from "../../../../services/legacy/countiesService";
import statesService from "../../../../services/legacy/statesService";
import countriesService from "../../../../services/legacy/countriesService";
import commissionSalesGroupsService from "../../../../services/legacy/commissionSalesGroupsService";
import priceDiscGroupsService from "../../../../services/legacy/priceDiscGroupsService";
import customerPaymentModesService from "../../../../services/legacy/customerPaymentModesService";
import paymentDaysService from "../../../../services/legacy/paymentDaysService";
import taxGroupHeadingService from "../../../../services/legacy/taxGroupHeadingService";
import customerStatisticsGroupsService from "../../../../services/legacy/customerStatisticsGroupsService";
import paymentSchedulesService from "../../../../services/legacy/paymentSchedulesService";
import contactsService from "../../../../services/legacy/contactsService";
import lineofbusinessService from "../../../../services/legacy/lineofbusinessService";
import { faUsers } from "@fortawesome/free-solid-svg-icons";

// Joins that duplicate the header row once per joined child row (e.g. one row
// per sales line). When a value here is true, CrudManager groups those rows
// back into a single visual row by default (see table-config.yml
// joins.<key>.frontend.groupDuplicateRows). Users can still toggle this from
// the grid UI; their choice is persisted per-user and overrides this default.
const JOIN_ROW_GROUPING_DEFAULTS = {};

const DEFAULT_GRID_ROW_STYLE_RULES = [{"hover_text": "Cliente bloqueado", "style": {"backgroundColor": "#fd5c5a"}, "when": {"field": "blocked", "operator": "ne", "value": 0}}];

const defaultGridRowStyleResolver = (row) => {
  if (!row || typeof row !== "object") {
    return null;
  }

  const getRowFieldValue = (row, fieldName) => {
    if (!row || typeof row !== "object" || !fieldName) {
      return null;
    }
    if (Object.prototype.hasOwnProperty.call(row, fieldName)) {
      return row[fieldName];
    }
    const normalizedTarget = String(fieldName).replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    const rowKey = Object.keys(row).find(
      (key) => String(key).replace(/[^a-zA-Z0-9]/g, "").toLowerCase() === normalizedTarget,
    );
    return rowKey ? row[rowKey] : null;
  };

  const toFiniteNumber = (value) => {
    if (value === undefined || value === null || value === "") {
      return null;
    }
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const evaluate = (left, operator, right, tolerance = 0) => {
    const leftNumber = toFiniteNumber(left);
    const rightNumber = toFiniteNumber(right);
    const bothNumeric = leftNumber !== null && rightNumber !== null;
    switch (operator) {
      case "eq":
        if (bothNumeric) return Math.abs(leftNumber - rightNumber) <= 0.0001;
        return String(left ?? "") === String(right ?? "");
      case "ne":
        if (bothNumeric) return Math.abs(leftNumber - rightNumber) > 0.0001;
        return String(left ?? "") !== String(right ?? "");
      case "ne_present":
        if (left === undefined || left === null || left === "" || right === undefined || right === null || right === "") return false;
        if (bothNumeric) return Math.abs(leftNumber - rightNumber) > 0.0001;
        return String(left ?? "") !== String(right ?? "");
      case "blank_or_zero":
        return left === undefined || left === null || left === "" || (leftNumber !== null && Math.abs(leftNumber) <= 0.0001);
      case "outside_percent_tolerance":
        if (!bothNumeric || Math.abs(rightNumber) <= 0.0001) return false;
        return Math.abs(leftNumber - rightNumber) > Math.abs(rightNumber) * Number(tolerance || 0);
      case "gt":
        return Number(left) > Number(right);
      case "gte":
        return Number(left) >= Number(right);
      case "lt":
        return Number(left) < Number(right);
      case "lte":
        return Number(left) <= Number(right);
      case "in":
        return Array.isArray(right) && right.map((v) => String(v)).includes(String(left ?? ""));
      case "not_in":
        return Array.isArray(right) && !right.map((v) => String(v)).includes(String(left ?? ""));
      case "contains":
        return String(left ?? "").toLowerCase().includes(String(right ?? "").toLowerCase());
      case "starts_with":
        return String(left ?? "").toLowerCase().startsWith(String(right ?? "").toLowerCase());
      case "ends_with":
        return String(left ?? "").toLowerCase().endsWith(String(right ?? "").toLowerCase());
      default:
        return false;
    }
  };

  for (const rule of DEFAULT_GRID_ROW_STYLE_RULES) {
    const field = rule?.when?.field;
    const operator = rule?.when?.operator || "eq";
    const value = rule?.when?.value_field
      ? getRowFieldValue(row, rule.when.value_field)
      : rule?.when?.value;
    if (!field) {
      continue;
    }
    if (evaluate(getRowFieldValue(row, field), operator, value, rule?.when?.tolerance ?? rule?.tolerance)) {
      return rule?.hover_text
        ? { style: rule?.style || null, title: rule.hover_text }
        : rule?.style || null;
    }
  }

  return null;
};

const CustomersPageGen = ({ service, transformColumns, crudConfigOverride }) => {
  const formTabs = [{"tabId": "TabOverview", "label": "axFormLayout.custtable.tabs.taboverview", "order": 1, "groups": [{"groupId": "TabOverview_MainFields", "label": "axFormLayout.custtable.groups.taboverview.taboverview_mainfields", "order": 1, "fields": ["name", "namealias", "invoiceaccount", "custgroup", "currency"]}], "fields": []}, {"tabId": "TabGeneral", "label": "axFormLayout.custtable.tabs.tabgeneral", "order": 2, "groups": [{"groupId": "Customer", "label": "axFormLayout.custtable.groups.tabgeneral.customer", "order": 2, "fields": ["languageid", "custclassificationid"]}, {"groupId": "Credit", "label": "axFormLayout.custtable.groups.tabgeneral.credit", "order": 3, "fields": ["mandatorycreditlimit", "creditrating", "creditmax"]}, {"groupId": "Administration", "label": "axFormLayout.custtable.groups.tabgeneral.administration", "order": 4, "fields": ["blocked", "onetimecustomer", "statisticsgroup", "accountstatement", "inactive", "closed"]}], "fields": []}, {"tabId": "TabSetup", "label": "axFormLayout.custtable.tabs.tabsetup", "order": 3, "groups": [{"groupId": "Invoice", "label": "axFormLayout.custtable.groups.tabsetup.invoice", "order": 1, "fields": ["numbersequencegroup"]}, {"groupId": "GiroMoneyTransferSlip", "label": "axFormLayout.custtable.groups.tabsetup.giromoneytransferslip", "order": 2, "fields": ["girotype", "girotypeinterestnote", "girotypecollectionletter", "girotypeprojinvoice"]}, {"groupId": "Delivery", "label": "axFormLayout.custtable.groups.tabsetup.delivery", "order": 3, "fields": ["freightzone", "dlvterm", "dlvreason", "destinationcodeid", "salescalendarid"]}, {"groupId": "SalesTax", "label": "axFormLayout.custtable.groups.tabsetup.salestax", "order": 4, "fields": ["taxgroup", "vatnum", "incltax", "taxlicensenum"]}, {"groupId": "Intercompany", "label": "axFormLayout.custtable.groups.tabsetup.intercompany", "order": 7, "fields": ["intercompanyautocreateorders"]}], "fields": []}, {"tabId": "TabPageSales", "label": "axFormLayout.custtable.tabs.tabpagesales", "order": 4, "groups": [{"groupId": "SalesOrder", "label": "axFormLayout.custtable.groups.tabpagesales.salesorder", "order": 1, "fields": ["markupgroup", "inventlocation", "commissiongroup", "salesgroup", "salespoolid", "ouraccountnum"]}, {"groupId": "Discount", "label": "axFormLayout.custtable.groups.tabpagesales.discount", "order": 2, "fields": ["multilinedisc", "enddisc", "pricegroup", "linedisc"]}], "fields": []}, {"tabId": "TabAddress", "label": "axFormLayout.custtable.tabs.tabaddress", "order": 6, "groups": [{"groupId": "Address", "label": "axFormLayout.custtable.groups.tabaddress.address", "order": 1, "fields": ["street", "zipcode", "city", "county", "state", "country"]}], "fields": []}, {"tabId": "TabCommunication", "label": "axFormLayout.custtable.tabs.tabcommunication", "order": 7, "groups": [{"groupId": "ContactInfo", "label": "axFormLayout.custtable.groups.tabcommunication.contactinfo", "order": 1, "fields": ["phone", "phonelocal", "cellularphone", "pager", "telefax", "email", "sms", "url", "contactpersonid", "lineofbusinessid", "abreviado"]}], "fields": []}, {"tabId": "TabPayment", "label": "axFormLayout.custtable.tabs.tabpayment", "order": 8, "groups": [{"groupId": "Payment", "label": "axFormLayout.custtable.groups.tabpayment.payment", "order": 1, "fields": ["paymtermid", "paymmode", "paymspec", "paymsched", "paymdayid", "cashdisc", "bankaccount"]}], "fields": []}, {"tabId": "TabDimension", "label": "axFormLayout.custtable.tabs.tabdimension", "order": 10, "groups": [{"groupId": "Dimension", "label": "axFormLayout.custtable.groups.tabdimension.dimension", "order": 1, "fields": ["dimension"]}], "fields": []}];

  const baseColumns = [
    {
      key: "accountNum",
      label: "customers.accountNum",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: false,
      showInFilter: true,
      editable: false,
      disabled: false,
      type: "text",
      sortKey: "id.accountNum"    },
    {
      key: "name",
      label: "customers.name",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "lastname",
      label: "customers.lastname",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: false,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "vatnum",
      label: "customers.vatnum",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "invoiceaccount",
      label: "customers.invoiceaccount",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "street",
      label: "customers.street",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "city",
      label: "customers.city",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "zipcode",
      label: "customers.zipcode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "county",
      label: "customers.county",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: countiesService,
      optionLabel: "name",
      optionValue: "countyid",
      lookupSearchMode: "or",
      lookupDependsOn: [{"localField": "country", "remoteFilterField": "countryid"}, {"localField": "state", "remoteFilterField": "stateid"}]    },
    {
      key: "state",
      label: "customers.state",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: statesService,
      optionLabel: "name",
      optionValue: "stateid",
      lookupSearchMode: "or",
      lookupDependsOn: [{"localField": "country", "remoteFilterField": "countryid"}]    },
    {
      key: "country",
      label: "customers.country",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: countriesService,
      optionLabel: "name",
      optionValue: "countryid",
      lookupSearchMode: "or"    },
    {
      key: "salesgroup",
      label: "customers.salesgroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: commissionSalesGroupsService,
      optionLabel: "name",
      optionValue: "groupid",
      lookupSearchMode: "or"    },
    {
      key: "phone",
      label: "customers.phone",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "telefax",
      label: "customers.telefax",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "phone"    },
    {
      key: "blocked",
      label: "customers.blocked",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "CustVendorBlocked",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.CustVendorBlocked.0"}, {"value": 1, "label": "Factura", "labelKey": "axEnums.CustVendorBlocked.1"}, {"value": 2, "label": "Todo", "labelKey": "axEnums.CustVendorBlocked.2"}]    },
    {
      key: "pricegroup",
      label: "customers.pricegroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: priceDiscGroupsService,
      optionLabel: "name",
      optionValue: "groupid",
      lookupSearchMode: "or",
      filterType: "number"    },
    {
      key: "email",
      label: "customers.email",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "clienteestadisticas",
      label: "customers.clienteestadisticas",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "paymmode",
      label: "customers.paymmode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: customerPaymentModesService,
      optionLabel: "name",
      optionValue: "paymmode",
      lookupSearchMode: "or"    },
    {
      key: "paymdayid",
      label: "customers.paymdayid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: paymentDaysService,
      optionLabel: "description",
      optionValue: "paymdayid",
      lookupSearchMode: "or"    },
    {
      key: "linedisc",
      label: "customers.linedisc",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "enddisc",
      label: "customers.enddisc",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "obsinternas",
      label: "customers.obsinternas",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "obsagente",
      label: "customers.obsagente",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "obstransporte",
      label: "customers.obstransporte",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "obsalbaran",
      label: "customers.obsalbaran",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "obsfactura",
      label: "customers.obsfactura",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "url",
      label: "customers.url",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "url"    },
    {
      key: "cellularphone",
      label: "customers.cellularphone",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "phone"    },
    {
      key: "phonelocal",
      label: "customers.phonelocal",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "phone"    },
    {
      key: "taxgroup",
      label: "customers.taxgroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: taxGroupHeadingService,
      optionLabel: "taxgroupname",
      optionValue: "taxgroup",
      lookupSearchMode: "or"    },
    {
      key: "statisticsgroup",
      label: "customers.statisticsgroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: customerStatisticsGroupsService,
      optionLabel: "statgroupname",
      optionValue: "custstatisticsgroup",
      lookupSearchMode: "or"    },
    {
      key: "paymsched",
      label: "customers.paymsched",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: false,
      disabled: true,
      type: "select",
      relatedService: paymentSchedulesService,
      optionLabel: "description",
      optionValue: "name"    },
    {
      key: "namealias",
      label: "customers.namealias",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "contactpersonid",
      label: "customers.contactpersonid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: contactsService,
      optionLabel: "name",
      optionValue: "contactpersonid",
      lookupSearchMode: "or"    },
    {
      key: "ouraccountnum",
      label: "customers.ouraccountnum",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "perfac",
      label: "customers.perfac",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "markupgroup",
      label: "customers.markupgroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "obsbloqueo",
      label: "customers.obsbloqueo",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "fechaprecio",
      label: "customers.fechaprecio",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime",
      filterType: "date"    },
    {
      key: "modifieddate",
      label: "customers.modifieddate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "datetime",
      filterType: "date"    },
    {
      key: "modifiedby",
      label: "customers.modifiedby",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "createddate",
      label: "customers.createddate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "datetime",
      filterType: "date"    },
    {
      key: "createdby",
      label: "customers.createdby",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "metodoemail",
      label: "customers.metodoemail",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "email"    },
    {
      key: "metodofax",
      label: "customers.metodofax",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "phone"    },
    {
      key: "metodocorreo",
      label: "customers.metodocorreo",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "idaxpsaddrdelivery",
      label: "customers.idaxpsaddrdelivery",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "idaxpsaddrinvoice",
      label: "customers.idaxpsaddrinvoice",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "recId",
      label: "customers.recId",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: true,
      editable: false,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "dataAreaId",
      label: "customers.dataAreaId",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: true,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "isyncstatus",
      label: "customers.isyncstatus",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "custgroup",
      label: "customers.custgroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "paymtermid",
      label: "customers.paymtermid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "cashdisc",
      label: "customers.cashdisc",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "currency",
      label: "customers.currency",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "intercompanyautocreateorders",
      label: "customers.intercompanyautocreateorders",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "onetimecustomer",
      label: "customers.onetimecustomer",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "accountstatement",
      label: "customers.accountstatement",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "CustAccountStatement",
      options: [{"value": 0, "label": "Siempre", "labelKey": "axEnums.CustAccountStatement.0"}, {"value": 1, "label": "Trimestre", "labelKey": "axEnums.CustAccountStatement.1"}, {"value": 2, "label": "Cada seis meses", "labelKey": "axEnums.CustAccountStatement.2"}, {"value": 3, "label": "Anualmente", "labelKey": "axEnums.CustAccountStatement.3"}, {"value": 4, "label": "Nunca", "labelKey": "axEnums.CustAccountStatement.4"}]    },
    {
      key: "creditmax",
      label: "customers.creditmax",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "mandatorycreditlimit",
      label: "customers.mandatorycreditlimit",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "dimension",
      label: "customers.dimension",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "dimension2",
      label: "customers.dimension2",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "dimension3",
      label: "customers.dimension3",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "multilinedisc",
      label: "customers.multilinedisc",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "incltax",
      label: "customers.incltax",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "inventlocation",
      label: "customers.inventlocation",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "dlvterm",
      label: "customers.dlvterm",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "clearingperiod",
      label: "customers.clearingperiod",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "freightzone",
      label: "customers.freightzone",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "creditrating",
      label: "customers.creditrating",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "commissiongroup",
      label: "customers.commissiongroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: commissionSalesGroupsService,
      optionLabel: "name",
      optionValue: "groupid",
      lookupSearchMode: "or"    },
    {
      key: "bankaccount",
      label: "customers.bankaccount",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "salespoolid",
      label: "customers.salespoolid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "numbersequencegroup",
      label: "customers.numbersequencegroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "languageid",
      label: "customers.languageid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "lineofbusinessid",
      label: "customers.lineofbusinessid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: lineofbusinessService,
      optionLabel: "description",
      optionValue: "lineofbusinessid",
      lookupDisplayFields: ["lineofbusinessid", "description"],
      lookupSearchFields: ["lineofbusinessid", "description"],
      lookupSearchMode: "and"    },
    {
      key: "destinationcodeid",
      label: "customers.destinationcodeid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "girotype",
      label: "customers.girotype",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "PaymentStub",
      options: [{"value": 0, "label": "Ninguno", "labelKey": "axEnums.PaymentStub.0"}, {"value": 1, "label": "FIK 65", "labelKey": "axEnums.PaymentStub.1"}, {"value": 2, "label": "BBS", "labelKey": "axEnums.PaymentStub.2"}, {"value": 3, "label": "Resguardo azul", "labelKey": "axEnums.PaymentStub.3"}, {"value": 4, "label": "Resguardo rojo", "labelKey": "axEnums.PaymentStub.4"}, {"value": 5, "label": "FIK 762", "labelKey": "axEnums.PaymentStub.5"}, {"value": 6, "label": "Resguardo naranja", "labelKey": "axEnums.PaymentStub.6"}, {"value": 7, "label": "S101", "labelKey": "axEnums.PaymentStub.7"}, {"value": 8, "label": "S102", "labelKey": "axEnums.PaymentStub.8"}, {"value": 9, "label": "Finlandés", "labelKey": "axEnums.PaymentStub.9"}]    },
    {
      key: "girotypeinterestnote",
      label: "customers.girotypeinterestnote",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "PaymentStub",
      options: [{"value": 0, "label": "Ninguno", "labelKey": "axEnums.PaymentStub.0"}, {"value": 1, "label": "FIK 65", "labelKey": "axEnums.PaymentStub.1"}, {"value": 2, "label": "BBS", "labelKey": "axEnums.PaymentStub.2"}, {"value": 3, "label": "Resguardo azul", "labelKey": "axEnums.PaymentStub.3"}, {"value": 4, "label": "Resguardo rojo", "labelKey": "axEnums.PaymentStub.4"}, {"value": 5, "label": "FIK 762", "labelKey": "axEnums.PaymentStub.5"}, {"value": 6, "label": "Resguardo naranja", "labelKey": "axEnums.PaymentStub.6"}, {"value": 7, "label": "S101", "labelKey": "axEnums.PaymentStub.7"}, {"value": 8, "label": "S102", "labelKey": "axEnums.PaymentStub.8"}, {"value": 9, "label": "Finlandés", "labelKey": "axEnums.PaymentStub.9"}]    },
    {
      key: "girotypecollectionletter",
      label: "customers.girotypecollectionletter",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "PaymentStub",
      options: [{"value": 0, "label": "Ninguno", "labelKey": "axEnums.PaymentStub.0"}, {"value": 1, "label": "FIK 65", "labelKey": "axEnums.PaymentStub.1"}, {"value": 2, "label": "BBS", "labelKey": "axEnums.PaymentStub.2"}, {"value": 3, "label": "Resguardo azul", "labelKey": "axEnums.PaymentStub.3"}, {"value": 4, "label": "Resguardo rojo", "labelKey": "axEnums.PaymentStub.4"}, {"value": 5, "label": "FIK 762", "labelKey": "axEnums.PaymentStub.5"}, {"value": 6, "label": "Resguardo naranja", "labelKey": "axEnums.PaymentStub.6"}, {"value": 7, "label": "S101", "labelKey": "axEnums.PaymentStub.7"}, {"value": 8, "label": "S102", "labelKey": "axEnums.PaymentStub.8"}, {"value": 9, "label": "Finlandés", "labelKey": "axEnums.PaymentStub.9"}]    },
    {
      key: "girotypeprojinvoice",
      label: "customers.girotypeprojinvoice",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "PaymentStubProj",
      options: [{"value": 0, "label": "Ninguno", "labelKey": "axEnums.PaymentStubProj.0"}, {"value": 9, "label": "Finlandés", "labelKey": "axEnums.PaymentStubProj.9"}]    },
    {
      key: "taxlicensenum",
      label: "customers.taxlicensenum",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "paymspec",
      label: "customers.paymspec",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "cityAx",
      label: "customers.cityAx",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "streetAx",
      label: "customers.streetAx",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "pager",
      label: "customers.pager",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "sms",
      label: "customers.sms",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "delRefzipcode",
      label: "customers.delRefzipcode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "dlvreason",
      label: "customers.dlvreason",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "salescalendarid",
      label: "customers.salescalendarid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "custclassificationid",
      label: "customers.custclassificationid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "inactive",
      label: "customers.inactive",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "closed",
      label: "customers.closed",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "abreviado",
      label: "customers.abreviado",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "defaultdirectdebitmandate",
      label: "customers.defaultdirectdebitmandate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "observationstarifa",
      label: "customers.observationstarifa",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "textarea",
      filterType: "text"    },
  ];

  const columns = transformColumns ? transformColumns(baseColumns) : baseColumns;
  const initialFormData = columns.reduce((acc, col) => {
    if (
      col &&
      col.showInForm &&
      Object.prototype.hasOwnProperty.call(col, "defaultValue")
    ) {
      acc[col.key] = col.defaultValue;
    }
    return acc;
  }, {});

  const defaultCrudConfig = {
    canCreate: true,
    canEdit: true,
    canDelete: false
  };

  const crudConfig = {
    ...defaultCrudConfig,
    gridRowStyleResolver: defaultGridRowStyleResolver,
    ...(crudConfigOverride || {})
  };

  return (
    <CrudManager
      service={service}
      columns={columns}
      entityName="customers"
      permissionPrefix="legacy.customers"
      icon={faUsers}
      formLayout="modal"
      canCreate={crudConfig.canCreate}
      canEdit={crudConfig.canEdit}
      canDelete={crudConfig.canDelete}
      initialFilters={crudConfig.initialFilters || {}}
      initialSort={crudConfig.initialSort || null}
      gridRowStyleResolver={crudConfig.gridRowStyleResolver || null}
      joinRowGroupingDefaults={JOIN_ROW_GROUPING_DEFAULTS}
      renderForm={crudConfig.renderForm || null}
      modalClassName={crudConfig.modalClassName || ""}
      formTabs={formTabs}
      initialFormData={initialFormData}
    />
  );
};

export default CustomersPageGen;